<template>
  <div>
    <BaseCard v-if="customersSegment && customersSegment.length">
      <BaseTable
        :headers="[$t('global.name'), $t('global.mobile'), $t('global.email'), $t('customers.segments.last_message')]"
        :rows="customersSegment.map((customer) => ({ 
          id: customer.id, 
          routerLink: {
            name: 'customer',
            params: {
              customerId: customer.id
            },
          },
          cells: [customer.fullName, customer.mobilePhone, customer.email, customer.lastMessageAt ? filters.dateTime(customer.lastMessageAt) : null] 
        }))"
        @select="customerSegmentReferrer = segmentKey"
        v-test="'customer-segment-list'"
      />
    </BaseCard>
    <LoadMore
      v-show="!allDataFetched"
      @shown="fetchMore"
    />
    <div
      v-if="allDataFetched && !loading && !customersSegment.length"
      :class="$style.noResults"
    >
      <img src="/img/empty-states/customers-segments.svg">
      {{ $t('customers.segments.no_results') }}
    </div>
  </div>
</template>

<script
  lang="ts"
  setup
>
import { useSessionStorage } from '@vueuse/core';
import { onBeforeRouteLeave } from 'vue-router';
import { usePagination } from '@/apollo/pagination';
import LoadMore from '@/components/LoadMore.vue';
import filters from '@/filters';
import type { Customer } from '@/types';
import { GET_CUSTOMERS_SEGMENT } from './graphql';

const emit = defineEmits(['update:modelValue']);

const props = defineProps<{
  segmentKey: string;
  modelValue: boolean;
}>();

const {
  allDataFetched,
  firstFetchFinished,
  fetchMore,
  variables,
  loading,
  onResult,
} = usePagination({
  fieldName: 'customersSegment',
  query: GET_CUSTOMERS_SEGMENT,
  variables: {
    segmentName: props.segmentKey,
  },
});

const customersSegment = ref<Customer[]>([]);
onResult(({ data }) => {
  customersSegment.value = data?.customersSegment || [];
  emit('update:modelValue', customersSegment.value.length > 0);
});

watch(
  () => props.segmentKey,
  () => {
    customersSegment.value = [];
    variables.segmentName = props.segmentKey;
  },
);

const customerSegmentReferrer = useSessionStorage<string | null>(
  'customer-segment-referrer',
  null,
);
if (customerSegmentReferrer.value) {
  customerSegmentReferrer.value = null;
}

onBeforeRouteLeave((to) => {
  if (to.name !== 'customer-overview') {
    customerSegmentReferrer.value = null;
  }
});
</script>

<style
  lang="scss"
  module
>
.noResults {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $spacing;
  text-align: center;
  padding: $spacing;
}
</style>
